'use client';

import { Label, RadioGroup, RadioGroupItem, Typography } from '@musinsa/mds';
import type { SeedingDeliveryAddressItem } from '@musinsa/services-curator';

const GUIDE_TEXT =
  '배송지 변경은 기본 배송지의 경우 마이 > 회원 정보 수정에서, 새로운 배송지 추가나 수정은 주문서 > 배송지 변경 페이지를 이용해 주세요.';

interface AddressRowProps {
  address: SeedingDeliveryAddressItem;
  onSelect: (address: SeedingDeliveryAddressItem) => void;
}

/**
 * 배송지 1행 — 좌측 Radio + (별칭/기본배송지 badge / 주소 / 연락처).
 * 행 클릭 시 onSelect 를 **직접 호출**한다. RadioGroupItem 의 click→onValueChange 에 의존하면
 * 이미 선택된(기본=대표) 행을 다시 탭할 때 Radix Radio 가 already-checked 가드(`checked || onCheck()`)
 * 로 onValueChange 를 발화하지 않아 선택(및 router.back)이 기록되지 않던 버그를 피한다. (CMQA-7155)
 * radio 영역 클릭은 stopPropagation 으로 행 onClick 이중 발화를 막고 dot 의 onValueChange 경로(키보드 포함)를 유지.
 */
const AddressRow = ({ address, onSelect }: AddressRowProps) => {
  return (
    <div
      role="presentation"
      onClick={() => onSelect(address)}
      className="flex cursor-pointer gap-[10px] px-[16px]!"
    >
      <div
        role="presentation"
        className="mt-[2px] flex items-start"
        onClick={(event) => event.stopPropagation()}
      >
        <RadioGroupItem value={String(address.id)} />
      </div>
      <div className="flex min-w-0 flex-col gap-[2px]!">
        <div className="flex min-w-0 items-center gap-[6px]!">
          {/* 배송지명 / 수령인 병기 — 상세는 수령인만 노출하므로 매칭 위해 수령인 항상 포함.
              배송지명(nullable) 없으면 구분자 없이 수령인 단독(댕글링 '/' 방지). (NOTICKET 배송지 표시 통일) */}
          <Typography variant="body14pxMed" colorScheme="black" className="min-w-0 truncate">
            {address.addressName ? `${address.addressName} / ${address.name}` : address.name}
          </Typography>
          {address.isPrimary && (
            <Label variant="grayOpacity" className="shrink-0">
              <Typography size="11" colorScheme="gray-600">
                기본 배송지
              </Typography>
            </Label>
          )}
        </div>
        <Typography variant="body13pxReg" colorScheme="gray-600">
          {address.address2 ? `${address.address1} ${address.address2}` : address.address1}
        </Typography>
        <Typography variant="body13pxReg" colorScheme="gray-600">
          {address.phone}
        </Typography>
      </div>
    </div>
  );
};

interface SeedingDeliveryAddressSelectProps {
  addresses: SeedingDeliveryAddressItem[];
  /** 현재 선택된 배송지 id (null 이면 미선택). */
  selectedId: number | null;
  onSelect: (address: SeedingDeliveryAddressItem) => void;
  className?: string;
}

/**
 * 29CM 배송지 변경 화면 — 큐레이터 등록 배송지 라디오 선택 (Figma 1375-99445).
 * 상단 안내문구 + MDS RadioGroup 배송지 목록(기본 배송지 Label). 무신사는 order-group opener 사용.
 */
export const SeedingDeliveryAddressSelect = ({
  addresses,
  selectedId,
  onSelect,
  className,
}: SeedingDeliveryAddressSelectProps) => (
  // 부모(PageLayout29CM)의 content 컨테이너가 bg-gray-100 이라, 배송지 목록 영역은 흰 배경으로 덮는다.
  <div className={className ?? 'flex grow flex-col bg-white'}>
    {/* 상단 안내텍스트 — Figma node 2603-134308: gray-700(#2A2A2A) + pt-24/pb-12. (SID-4428, 29CM 한정) */}
    <div className="px-[16px]! pt-[24px]! pb-[12px]!">
      <Typography variant="body13pxReg" colorScheme="gray-700" asChild>
        <p>{GUIDE_TEXT}</p>
      </Typography>
    </div>
    <RadioGroup
      value={selectedId != null ? String(selectedId) : undefined}
      onValueChange={(value) => {
        const found = addresses.find((address) => address.id === Number(value));
        if (found) onSelect(found);
      }}
      className="pt-[12px]! pb-[72px]! gap-y-[12px]!"
    >
      {addresses.map((address) => (
        <AddressRow key={address.id} address={address} onSelect={onSelect} />
      ))}
    </RadioGroup>
  </div>
);
